import React, { useEffect, useState } from "react";
import { FooterComponents } from "../FooterComponent";
import SidebarComponent from "../SidbarComponent";
import TopBarComponent from "../TopBarComponent";
import { Plus, Trash2, Save, Edit, X, CheckCircle, AlertCircle, BarChart3, Target } from "lucide-react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getToken } from '../../utils/auth';

export default function AbroadSelectionEvaluationScale() {
  const { criteriaId } = useParams();
  const navigate = useNavigate();
  
  const [evaluationScales, setEvaluationScales] = useState([]);
  const [criteriaInfo, setCriteriaInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [newScale, setNewScale] = useState({
    scaleGrade: "",
    minScore: "",
    maxScore: "",
    marksAwarded: ""
  });

  const [editFormData, setEditFormData] = useState({
    id: "",
    scaleGrade: "",
    minScore: "",
    maxScore: "",
    marksAwarded: ""
  });

  useEffect(() => {
    fetchEvaluationScales();
    fetchCriteriaInfo();
  }, [criteriaId]);

  const fetchCriteriaInfo = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}hec/abroad-selection-criteria/get-by-id?id=${criteriaId}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      setCriteriaInfo(response.data);
    } catch (err) {
      console.error("Error fetching criteria info:", err);
    }
  };

  const fetchEvaluationScales = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}hec/abroad-selection-evaluation-scale/get-by-criteria?criteriaId=${criteriaId}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      setEvaluationScales(response.data || []);
    } catch (err) {
      setError("Failed to fetch evaluation scales: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, isEdit = false) => {
    const { name, value } = e.target;
    if (isEdit) {
      setEditFormData(prev => ({
        ...prev,
        [name]: ["minScore", "maxScore", "marksAwarded"].includes(name) ? parseInt(value) || "" : value
      }));
    } else {
      setNewScale(prev => ({
        ...prev,
        [name]: ["minScore", "maxScore", "marksAwarded"].includes(name) ? parseInt(value) || "" : value
      }));
    }
  };

  const validateForm = (data) => {
    if (!data.scaleGrade.trim()) {
      toast.error("Scale grade is required");
      return false;
    }
    
    if (data.minScore === "" || data.minScore < 0) {
      toast.error("Minimum score must be 0 or greater");
      return false;
    }
    
    if (data.maxScore === "" || data.maxScore < 0) {
      toast.error("Maximum score must be 0 or greater");
      return false;
    }
    
    if (data.minScore > data.maxScore) {
      toast.error("Minimum score cannot be greater than maximum score");
      return false;
    }
    
    if (data.marksAwarded === "" || data.marksAwarded < 0) {
      toast.error("Marks awarded must be 0 or greater");
      return false;
    }

    // Check for overlapping ranges
    const overlapping = evaluationScales.some(scale => {
      // Skip current scale when editing
      if (editingId && scale.id === editingId) {
        return false;
      }
      
      return (
        (data.minScore >= scale.minScore && data.minScore <= scale.maxScore) ||
        (data.maxScore >= scale.minScore && data.maxScore <= scale.maxScore) ||
        (data.minScore <= scale.minScore && data.maxScore >= scale.maxScore)
      );
    });

    if (overlapping) {
      toast.error("Score range overlaps with existing evaluation scale");
      return false;
    }
    
    return true;
  };

  const handleAddScale = async () => {
    if (!validateForm(newScale)) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}hec/abroad-selection-evaluation-scale/create?criteriaId=${criteriaId}`,
        {
          scaleGrade: newScale.scaleGrade,
          minScore: parseInt(newScale.minScore),
          maxScore: parseInt(newScale.maxScore),
          marksAwarded: parseInt(newScale.marksAwarded)
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success("Evaluation scale added successfully!");
        setNewScale({
          scaleGrade: "",
          minScore: "",
          maxScore: "",
          marksAwarded: ""
        });
        setIsAddingNew(false);
        fetchEvaluationScales();
      }
    } catch (err) {
      console.error("Error adding evaluation scale:", err);
      toast.error(err.response?.data?.message || "Failed to add evaluation scale");
    }
  };

  const handleEditScale = (scale) => {
    setEditingId(scale.id);
    setEditFormData({
      id: scale.id,
      scaleGrade: scale.scaleGrade,
      minScore: scale.minScore,
      maxScore: scale.maxScore,
      marksAwarded: scale.marksAwarded
    });
  };

  const handleUpdateScale = async () => {
    if (!validateForm(editFormData)) return;

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}hec/abroad-selection-evaluation-scale/update`,
        {
          id: editFormData.id,
          scaleGrade: editFormData.scaleGrade,
          minScore: parseInt(editFormData.minScore),
          maxScore: parseInt(editFormData.maxScore),
          marksAwarded: parseInt(editFormData.marksAwarded)
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success("Evaluation scale updated successfully!");
        setEditingId(null);
        setEditFormData({
          id: "",
          scaleGrade: "",
          minScore: "",
          maxScore: "",
          marksAwarded: ""
        });
        fetchEvaluationScales();
      }
    } catch (err) {
      console.error("Error updating evaluation scale:", err);
      toast.error(err.response?.data?.message || "Failed to update evaluation scale");
    }
  };

  const handleDeleteScale = async (scaleId) => {
    if (window.confirm("Are you sure you want to delete this evaluation scale?")) {
      try {
        const response = await axios.delete(
          `${process.env.REACT_APP_BASE_URL}hec/abroad-selection-evaluation-scale/delete?id=${scaleId}`,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          }
        );

        if (response.status === 200) {
          toast.success("Evaluation scale deleted successfully!");
          fetchEvaluationScales();
        }
      } catch (err) {
        console.error("Error deleting evaluation scale:", err);
        toast.error(err.response?.data?.message || "Failed to delete evaluation scale");
      }
    }
  };

  const cancelAdd = () => {
    setIsAddingNew(false);
    setNewScale({
      scaleGrade: "",
      minScore: "",
      maxScore: "",
      marksAwarded: ""
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditFormData({
      id: "",
      scaleGrade: "",
      minScore: "",
      maxScore: "",
      marksAwarded: ""
    });
  };

  const getScaleRangeColor = (scale) => {
    const percentage = (scale.maxScore + scale.minScore) / 2;
    if (percentage >= 80) return "bg-green-100 text-green-800";
    if (percentage >= 60) return "bg-blue-100 text-blue-800";
    if (percentage >= 40) return "bg-yellow-100 text-yellow-800";
    return "bg-red-100 text-red-800";
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700"
        aria-label="Sidebar"
      >
        <SidebarComponent />
      </aside>
      <div className="pt-6 sm:ml-64">
        <div className="p-6 min-h-screen">
          <div className="max-w-7xl mx-auto py-8 mt-10">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Evaluation Scale Management
                  </h1>
                  <p className="text-gray-600">
                    Define scoring scales for: {criteriaInfo?.criteriaName || "Loading..."}
                  </p>
                  {criteriaInfo && (
                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Target className="w-4 h-4 mr-1" />
                        Weight: {criteriaInfo.weightAllocated}%
                      </span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  ← Back to Criteria
                </button>
              </div>
            </div>

            {/* Add New Button */}
            <div className="mb-6">
              <button
                onClick={() => setIsAddingNew(true)}
                disabled={isAddingNew}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Evaluation Scale
              </button>
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-spin">
                  <BarChart3 className="w-8 h-8 text-indigo-600" />
                </div>
                <p className="text-gray-600">Loading evaluation scales...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12 bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
                <p className="font-medium">Error: {error}</p>
                <p>Please try again later.</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Scale Grade
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Score Range
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Marks Awarded
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {/* Add New Form Row */}
                      {isAddingNew && (
                        <tr className="bg-blue-50">
                          <td className="px-6 py-4">
                            <input
                              type="text"
                              name="scaleGrade"
                              value={newScale.scaleGrade}
                              onChange={(e) => handleInputChange(e, false)}
                              placeholder="e.g., Excellent, Good, Poor"
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex space-x-2">
                              <input
                                type="number"
                                name="minScore"
                                value={newScale.minScore}
                                onChange={(e) => handleInputChange(e, false)}
                                placeholder="Min"
                                min="0"
                                className="w-20 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                              <span className="flex items-center text-gray-500">to</span>
                              <input
                                type="number"
                                name="maxScore"
                                value={newScale.maxScore}
                                onChange={(e) => handleInputChange(e, false)}
                                placeholder="Max"
                                min="0"
                                className="w-20 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <input
                              type="number"
                              name="marksAwarded"
                              value={newScale.marksAwarded}
                              onChange={(e) => handleInputChange(e, false)}
                              placeholder="Marks"
                              min="0"
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex justify-center space-x-2">
                              <button
                                onClick={handleAddScale}
                                className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                <Save className="w-4 h-4 mr-2" />
                                Save
                              </button>
                              <button
                                onClick={cancelAdd}
                                className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                              >
                                <X className="w-4 h-4 mr-2" />
                                Cancel
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Existing Scales Rows */}
                      {evaluationScales.map((scale) => (
                        <tr key={scale.id} className={editingId === scale.id ? "bg-yellow-50" : "hover:bg-gray-50"}>
                          <td className="px-6 py-4">
                            {editingId === scale.id ? (
                              <input
                                type="text"
                                name="scaleGrade"
                                value={editFormData.scaleGrade}
                                onChange={(e) => handleInputChange(e, true)}
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            ) : (
                              <div className="flex items-center">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getScaleRangeColor(scale)}`}>
                                  {scale.scaleGrade}
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {editingId === scale.id ? (
                              <div className="flex space-x-2">
                                <input
                                  type="number"
                                  name="minScore"
                                  value={editFormData.minScore}
                                  onChange={(e) => handleInputChange(e, true)}
                                  min="0"
                                  className="w-20 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                />
                                <span className="flex items-center text-gray-500">to</span>
                                <input
                                  type="number"
                                  name="maxScore"
                                  value={editFormData.maxScore}
                                  onChange={(e) => handleInputChange(e, true)}
                                  min="0"
                                  className="w-20 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                />
                              </div>
                            ) : (
                              <span className="text-sm text-gray-900">
                                {scale.minScore} - {scale.maxScore}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {editingId === scale.id ? (
                              <input
                                type="number"
                                name="marksAwarded"
                                value={editFormData.marksAwarded}
                                onChange={(e) => handleInputChange(e, true)}
                                min="0"
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            ) : (
                              <span className="text-sm font-medium text-gray-900">
                                {scale.marksAwarded}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center">
                            {editingId === scale.id ? (
                              <div className="flex justify-center space-x-2">
                                <button
                                  onClick={handleUpdateScale}
                                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                >
                                  <Save className="w-4 h-4 mr-2" />
                                  Update
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                >
                                  <X className="w-4 h-4 mr-2" />
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="flex justify-center space-x-2">
                                <button
                                  onClick={() => handleEditScale(scale)}
                                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </button>
                                {/* <button
                                  onClick={() => handleDeleteScale(scale.id)}
                                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </button> */}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}

                      {evaluationScales.length === 0 && !isAddingNew && (
                        <tr>
                          <td colSpan="4" className="px-6 py-12 text-center">
                            <div className="text-gray-500">
                              <Target className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                              <h3 className="text-lg font-medium text-gray-900 mb-2">
                                No evaluation scales found
                              </h3>
                              <p>Get started by adding your first evaluation scale.</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
        <div>
          <FooterComponents />
        </div>
      </div>
    </div>
  );
}